import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { setClientSettings } from "./clientSettingsSlice";

// Define a type for the slice state
export interface AuthState {
  isAuthenticated: boolean;
  isFirstLogin: boolean;
  token: string | null;
  clientId: string | null;
  userId: string | null;
  userName: string | null;
  firstName: string | null;
  lastName: string | null;
  exp: number | null;
  permissions: number[];
  cultureId: string | null;
  ipAddress: string | null;
}

// Define the initial state using that type
const initialState: AuthState = {
  isAuthenticated: false,
  isFirstLogin: false,
  clientId: null,
  userId: null,
  userName: null,
  firstName: null,
  lastName: null,
  cultureId: null,
  permissions: [],
  exp: null,
  token: null,
  ipAddress: null,
};

export const authSlice = createSlice({
  name: "auth",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    login(
      state,
      action: PayloadAction<{
        clientId: string | null;
        userId: string | null;
        userName: string | null;
        firstName: string | null;
        lastName: string | null;
        exp: number | null;
        permissions: number[] | [];
        cultureId: string | null;
        token: string | null;
        ipAddress: string | null;
      }>,
    ) {
      state.isAuthenticated = true;
      state.userName = action.payload.userName;
      state.firstName = action.payload.firstName;
      state.lastName = action.payload.lastName;
      state.clientId = action.payload.clientId;
      state.userId = action.payload.userId;
      state.cultureId = action.payload.cultureId;
      state.permissions = action.payload.permissions;
      state.exp = action.payload.exp;
      state.token = action.payload.token;
      state.ipAddress = action.payload.ipAddress;
    },
    logout(state) {
      state.isAuthenticated = false;
      state.userName = null;
      state.firstName = null;
      state.lastName = null;
      state.token = null;
      state.clientId = null;
      state.userId = null;
      state.cultureId = null;
      state.permissions = [];
      state.exp = null;
      state.ipAddress = null;
    },
    setPermissions(
      state,
      action: PayloadAction<{
        permissions: number[];
      }>,
    ) {
      state.permissions = action.payload.permissions;
    },
  },
});

export const { login, logout, setPermissions } = authSlice.actions;

// Other code such as selectors can use the imported `RootState` type

export const getAuthState = (state: RootState) => state.auth;
export const getAuthStatus = (state: RootState) => state.auth.isAuthenticated;

export default authSlice.reducer;
